import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  OKTA_CLIENT_ID,
  OKTA_REDIRECT_URL,
  OKTA_URL,
  STORAGE_KEY,
} from "@/lib/auth/constants";
import {
  computeCodeChallenge,
  generateCodeVerifier,
  generateOAuthState,
} from "@/lib/auth/helpers";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function missingOktaConfig(): string | null {
  if (!OKTA_URL?.trim()) return "VITE_OKTA_URL";
  if (!OKTA_CLIENT_ID?.trim()) return "VITE_OKTA_CLIENT_ID";
  if (!OKTA_REDIRECT_URL?.trim()) return "VITE_OKTA_REDIRECT_URL";
  return null;
}

function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const missing = missingOktaConfig();

  useEffect(() => {
    if (missing) return;

    const debug =
      new URLSearchParams(window.location.search).get("debug") === "1";

    if (debug) {
      console.info("[northstar login] config", {
        oktaUrl: OKTA_URL,
        clientId: OKTA_CLIENT_ID,
        redirectUri: OKTA_REDIRECT_URL,
      });
    }

    async function redirect() {
      try {
        const verifier = await generateCodeVerifier();
        const challenge = await computeCodeChallenge(verifier);
        const state = generateOAuthState();
        sessionStorage.setItem(STORAGE_KEY.CODE_VERIFIER, verifier);
        sessionStorage.setItem(STORAGE_KEY.OAUTH_STATE, state);

        const params = new URLSearchParams({
          client_id: OKTA_CLIENT_ID,
          redirect_uri: OKTA_REDIRECT_URL,
          scope: "openid profile email offline_access",
          response_type: "code",
          state,
          code_challenge: challenge,
          code_challenge_method: "S256",
        });

        const target = `${OKTA_URL}?${params}`;
        if (debug) console.info("[northstar login] navigating to", target);
        window.location.assign(target);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Could not start Okta redirect";
        console.error("[northstar login]", e);
        setError(message);
      }
    }

    void redirect();
  }, [missing]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      {missing ? (
        <div className="flex max-w-md flex-col gap-3 text-center">
          <p className="text-destructive text-sm">
            Missing {missing} at build time. Rebuild with a .env file that sets
            all VITE_OKTA_* variables (they are inlined when you run vite
            build).
          </p>
          <p className="text-muted-foreground text-xs">
            Open DevTools → Console for details. Add{" "}
            <code className="bg-muted rounded px-1">?debug=1</code> to the URL
            for config logging.
          </p>
        </div>
      ) : error ? (
        <div className="flex max-w-md flex-col gap-3 text-center">
          <p className="text-destructive text-sm">{error}</p>
          <p className="text-muted-foreground text-xs">
            Open DevTools → Console for details. Add{" "}
            <code className="bg-muted rounded px-1">?debug=1</code> to the URL
            for config logging.
          </p>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Redirecting to login…</p>
      )}
    </div>
  );
}
